import { HTTPError } from 'ky';
import { useTranslation } from 'react-i18next';
import { useApiProblem, type BookingPaymentHistoryItem } from '@/shared/api';
import { Button } from '@/shared/components';
import { StripePaymentHistoryRow } from './StripePaymentHistoryRow';

interface Props {
  rows?: BookingPaymentHistoryItem[];
  loading?: boolean;
  paused?: boolean;
  error?: unknown;
  refreshing?: boolean;
  onRetry: () => void;
}
export function StripePaymentHistoryContent({
  rows,
  loading,
  paused,
  error,
  refreshing,
  onRetry,
}: Props) {
  const { t } = useTranslation('stripePayments');
  const problem = useApiProblem(error);
  const status =
    error instanceof HTTPError ? error.response.status : error ? problem?.status : undefined;
  if (status === 401 || status === 403 || status === 404) {
    return (
      <p role="alert" className="text-ink-soft text-sm">
        {t(status === 404 ? 'missing' : 'denied')}
      </p>
    );
  }
  if (!rows && paused) return <p className="text-ink-soft text-sm">{t('offline')}</p>;
  if (!rows && loading && !error)
    return (
      <p role="status" className="text-ink-soft text-sm">
        {t('loading')}
      </p>
    );
  const reason =
    problem?.status === 0
      ? t('network')
      : problem?.status === 403
        ? t('denied')
        : problem?.status === 404
          ? t('missing')
          : problem?.title
            ? `(${problem.title})`
            : '';
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {!!error && (
        <div
          role="alert"
          className="border-hairline rounded-tile flex flex-col items-start gap-3 border p-4 text-sm"
        >
          <p>
            {rows ? t('stale') : t('error')} {reason}
          </p>
          {problem?.status !== 403 && problem?.status !== 404 && (
            <Button variant="secondary" className="min-h-11" onClick={onRetry} loading={refreshing}>
              {t('retry')}
            </Button>
          )}
        </div>
      )}
      {rows && rows.length === 0 && <p className="text-ink-soft text-sm">{t('empty')}</p>}
      {rows && rows.length > 0 && (
        <ul className="flex min-w-0 flex-col gap-3">
          {rows.map((row) => (
            <StripePaymentHistoryRow key={row.id} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
}
